import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight,
  Download, Printer, FileText, SlidersHorizontal, X
} from 'lucide-react';

const rowVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.02, duration: 0.15 },
  }),
};

export default function ReportTable({
  columns = [],
  data = [],
  summary,
  pageSize: defaultPageSize = 15,
  loading = false,
  onExportPDF,
  onExportExcel,
  onExportCSV,
  onPrint,
  className = '',
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [page, setPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState(columns.map((_, i) => i));
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / defaultPageSize));
  const paginatedData = sortedData.slice((page - 1) * defaultPageSize, page * defaultPageSize);
  const visibleCols = columns.filter((_, idx) => visibleColumns.includes(idx));

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ChevronsUpDown size={13} className="text-gray-300 dark:text-gray-600" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp size={13} className="text-primary-600 dark:text-primary-400" />
      : <ChevronDown size={13} className="text-primary-600 dark:text-primary-400" />;
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm ${className}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {data.length} records
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={onExportCSV}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Export CSV"
          >
            <Download size={12} /> CSV
          </button>
          <button
            onClick={onExportExcel}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Export Excel"
          >
            <FileText size={12} /> Excel
          </button>
          <button
            onClick={onExportPDF}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Export PDF"
          >
            <FileText size={12} /> PDF
          </button>
          <button
            onClick={onPrint}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Print"
          >
            <Printer size={12} />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/80">
              {visibleCols.map((col, idx) => (
                <th
                  key={col.key || idx}
                  onClick={() => col.sortable !== false && col.key && handleSort(col.key)}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                    col.sortable !== false && col.key ? 'cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200' : ''
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <span>{col.header}</span>
                    {col.sortable !== false && col.key && renderSortIcon(col.key)}
                  </div>
                </th>
              ))}
              <th className="w-10 px-2 py-3">
                <div className="relative">
                  <button
                    onClick={() => setColumnMenuOpen(!columnMenuOpen)}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400"
                  >
                    <SlidersHorizontal size={13} />
                  </button>
                  {columnMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                      {columns.map((col, idx) => (
                        <label key={col.key || idx} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={visibleColumns.includes(idx)}
                            onChange={() => {
                              setVisibleColumns((prev) =>
                                prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
                              );
                            }}
                            className="rounded border-gray-300 dark:border-gray-600 text-primary-600"
                          />
                          {col.header}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {visibleCols.map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${50 + Math.random() * 50}%` }} />
                    </td>
                  ))}
                  <td />
                </tr>
              ))
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length + 1} className="px-4 py-12 text-center text-gray-400 dark:text-gray-500">
                  <FileText size={32} className="mx-auto mb-2" />
                  <p className="text-sm font-medium">No data available</p>
                </td>
              </tr>
            ) : (
              <AnimatePresence mode="popLayout">
                {paginatedData.map((row, rowIdx) => (
                  <motion.tr
                    key={row.id || row._id || rowIdx}
                    custom={rowIdx}
                    variants={rowVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    layout
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    {visibleCols.map((col, colIdx) => (
                      <td key={col.key || colIdx} className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {col.render ? col.render(row[col.key], row, rowIdx) : row[col.key]}
                      </td>
                    ))}
                    <td className="px-2 py-3" />
                  </motion.tr>
                ))}
              </AnimatePresence>
            )}
          </tbody>
          {summary && !loading && paginatedData.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 dark:bg-gray-800/60 font-medium">
                {visibleCols.map((col, idx) => (
                  <td key={col.key || idx} className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    {col.summaryRender ? col.summaryRender(summary[col.key], summary) : idx === 0 ? 'Total' : ''}
                  </td>
                ))}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {data.length > defaultPageSize && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {(page - 1) * defaultPageSize + 1}-{Math.min(page * defaultPageSize, data.length)} of {data.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400 px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
